import { defaultMailApp, multiWindow, updates, windowChrome } from "@transport";
import type { Signal } from "@preact/signals";

import * as android from "../lib/android";
import * as chrome from "../lib/chrome";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";

import * as auth from "../lib/auth";
import { fileSize, plural, shortDate } from "../lib/format";
import * as install from "../lib/install";
import { looksLikeToken } from "../lib/jmap";
import type { RetentionStatus } from "../lib/net.types";
import * as nav from "../lib/navigation";
import * as priority from "../lib/priority";
import * as push from "../lib/push";
import { type DesktopDownload, desktopDownloads } from "../lib/release";
import * as retention from "../lib/retention";
import {
  AUTO_READ_MAX,
  autoReadSeconds,
  density,
  deviceToken,
  loadRemoteImages,
  openInNewWindow,
  readingPane,
  type SwipeAction,
  SWIPE_ACTIONS,
  swipeLeft,
  swipeRight,
  systemTitlebar,
  theme,
  touchCapable,
  UI_SCALES,
  uiScale,
  UNDO_SEND_MAX,
  undoSendSeconds,
  composeFormat,
  type ComposeFormat,
} from "../lib/settings";
import * as store from "../lib/store";
import * as updateWatch from "../lib/update-watch";

/**
 * The pages of the settings screen. `ui/SettingsShell.tsx` lays them out, names
 * them and decides which one is showing; each of these draws the inside of one
 * page and nothing else -- no heading, because the shell has already written
 * the page's name at the top of it, and no idea what is beside it.
 *
 * They were one stack of twelve cards until now, in the order they happened to
 * be written. Nothing below has changed except which page each card is on and,
 * where a card's heading had become the page's name, whether it still needs one
 * of its own.
 */
export function Appearance() {
  return (
    <>
      <p class="lede">
        Kept in this browser and nowhere else. Another browser, or the same mailbox on a phone, has
        its own answers to these.
      </p>

      <div class="card">
        <Row label="Theme">
          <Segmented
            value={theme.value}
            options={[
              ["system", "System"],
              ["light", "Light"],
              ["dark", "Dark"],
            ]}
            onPick={(v) => (theme.value = v as typeof theme.value)}
          />
        </Row>
        <Row label="Density">
          <Segmented
            value={density.value}
            options={[
              ["comfortable", "Comfortable"],
              ["compact", "Compact"],
            ]}
            onPick={(v) => (density.value = v as typeof density.value)}
          />
        </Row>
        {/*
          * Absent rather than disabled where the shell ignores the viewport
          * meta, which is every desktop build. See `lib/android.ts`.
          */}
        {android.scalesViewport() && (
          <Row label="Size">
            <Segmented
              value={String(uiScale.value)}
              options={UI_SCALES.map(([value, label]) => [String(value), label])}
              onPick={(v) => (uiScale.value = Number(v))}
            />
          </Row>
        )}
        <Row label="Reading pane">
          <Segmented
            value={readingPane.value}
            options={[
              ["side", "Beside the list"],
              ["off", "Full width"],
            ]}
            onPick={(v) => (readingPane.value = v as typeof readingPane.value)}
          />
        </Row>
        <Row label="Mark as read">
          <AutoRead />
        </Row>
        {/*
          Absent in the browser rather than disabled, because there the second
          click opens a real browser tab and there is nothing to choose between
          -- see `multiWindow` in `lib/net.types.ts`. A control offering two
          answers where only one exists is worse than no control.

          The setting itself has been here since tabs shipped and nothing ever
          drew it, so the choice was persisted, read on every double-click, and
          impossible to change.
        */}
        {/*
          Only where the app can actually draw a titlebar. Absent, not disabled,
          on the platforms where it cannot -- macOS wants its real traffic
          lights over a transparent bar rather than buttons of ours -- because a
          control that cannot change anything is worse than no control. Same
          rule as the offline pane.
        */}
        {chrome.available.value && (
          <Row label="Titlebar">
            <Segmented
              value={systemTitlebar.value ? "system" : "app"}
              options={[
                ["app", "Part of the app"],
                ["system", "System"],
              ]}
              onPick={(v) => void chrome.useSystemTitlebar(v === "system")}
            />
          </Row>
        )}
        {multiWindow && (
          <Row label="Double-click a message">
            <Segmented
              value={openInNewWindow.value ? "window" : "tab"}
              options={[
                ["tab", "Opens a tab"],
                ["window", "Opens a window"],
              ]}
              onPick={(v) => (openInNewWindow.value = v === "window")}
            />
          </Row>
        )}
        {/*
          Directly under the double-click setting, because they are the same
          question asked of two hands: that one binds the gesture a mouse has
          spare, these bind the ones a thumb has. Neither is drawn where its
          input does not exist, so on any given device at most one of the two
          groups is here.

          Absent and not disabled on a device with no touchscreen -- the rule
          the offline pane, the titlebar toggle and the double-click setting
          above all follow. `touchCapable()` asks the hardware rather than the
          build, because the same `src/` runs in a phone browser, in the Android
          shell and on a touchscreen laptop.
        */}
        {touchCapable() && (
          <>
            <Row label="Swipe left">
              <SwipeBinding label="Swipe a message left" bound={swipeLeft} />
            </Row>
            <Row label="Swipe right">
              <SwipeBinding label="Swipe a message right" bound={swipeRight} />
            </Row>
          </>
        )}
      </div>
    </>
  );
}

/**
 * What happens to a message on its way out, and what is offered as it is
 * addressed.
 *
 * The suggestion book is here rather than on a page of its own because it is
 * not a thing to be looked at: it is a list this app draws on while somebody
 * writes to someone, which is the same moment the undo window is about.
 */
export function Sending() {
  return (
    <>
      <ComposeFormatCard />
      <UndoSend />
      <SuggestionBook />
    </>
  );
}

/** Plain or rich, for a new message. Replies follow the original. */
function ComposeFormatCard() {
  return (
    <div class="card">
      <h2>Writing</h2>
      <Row label="New messages">
        <Segmented
          value={composeFormat.value}
          options={[
            ["plain", "Plain text"],
            ["rich", "Rich text"],
          ]}
          onPick={(v) => {
            composeFormat.value = v as ComposeFormat;
          }}
        />
      </Row>
      <p class="hint">Replies and forwards follow the original. Either can be switched while writing.</p>
    </div>
  );
}

/*
 * Off by default, because a remote image is a read receipt: fetching it tells
 * the sender the address is live and when it was opened. The screen says what
 * that costs; it does not argue the case.
 */
export function Privacy() {
  return (
    <>
      <p class="lede">
        One setting, and it is the one that decides whether reading a message tells the person who
        sent it. Kept in this browser, like the appearance settings.
      </p>

      <div class="card">
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <input
            type="checkbox"
            style={{ width: "auto", marginTop: 3 }}
            checked={loadRemoteImages.value}
            onChange={(e) => (loadRemoteImages.value = (e.target as HTMLInputElement).checked)}
          />
          <span>
            <strong>Always load remote images</strong>
            <div class="hint">Tells the sender you opened the message.</div>
          </span>
        </label>
      </div>
    </>
  );
}

/**
 * Who this browser is signed in as, and everything that can end that.
 *
 * The three cards were scattered through the old stack with Appearance between
 * them. They are one subject: a laptop that has gone missing is answered by
 * reading the device list, ending its session and changing the password, in
 * that order, and none of that is findable if the three live apart.
 */
export function SignIn() {
  return (
    <>
      <p class="lede">
        These live on the daemon rather than in this browser, which is why ending a session here
        ends it everywhere.
      </p>

      <Account />
      <Sessions />
      <Password />
      {/* After the password, because it is the same subject read one step
          further on: the password is what somebody knows, this is what they
          have, and the order on screen is the order they are asked for at the
          sign-in screen. */}
      <TwoFactor />
    </>
  );
}

/**
 * Which of the second factor's five faces this screen is showing.
 *
 * A function over the three pieces of state rather than a `switch` inside the
 * JSX, because the ordering is the whole design and it is not obvious:
 *
 * - **`unavailable` outranks everything.** A deployment with no master key
 *   cannot store a secret at all, so every control here would fail. It is the
 *   one state that is about the server rather than the account.
 * - **`enrolment` outranks `enabled`.** Somebody replacing a lost phone is
 *   enrolled *and* holding a fresh secret, and showing them the "it is on" card
 *   at that moment would hide the secret they are part-way through copying --
 *   with no way back to it, because the daemon will not repeat one.
 * - **`pending` without a secret in hand is its own state**, not "off". The
 *   daemon holds a half-finished enrolment that this screen cannot show,
 *   because the secret was only ever sent once; the honest offer is to start
 *   again, which replaces it.
 *
 * Exported and pure so the ordering can be tested without a DOM, the way
 * `shapeOf` in `AttachmentView.tsx` is.
 */
export type TotpPanel = "loading" | "unavailable" | "off" | "restart" | "enrolling" | "on";

export function totpPanel(view: {
  status: auth.TotpStatus | null;
  unavailable: boolean;
  enrolment: auth.TotpEnrolment | null;
}): TotpPanel {
  if (view.unavailable) return "unavailable";
  if (view.enrolment) return "enrolling";
  if (view.status === null) return "loading";
  if (view.status.enabled) return "on";
  return view.status.pending ? "restart" : "off";
}

/**
 * A base32 secret in groups of four, for somebody typing it by hand.
 *
 * Sixteen or thirty-two unbroken characters with no descenders and no vowels to
 * anchor on is a string people lose their place in, and this is the one path
 * that exists for a person whose phone cannot scan anything -- so the fallback
 * is exactly the case where legibility matters most.
 *
 * The spaces are presentation and nothing else. Every authenticator app strips
 * whitespace on entry, which is why this can be done to the displayed copy at
 * all; nothing here ever sends a grouped secret anywhere.
 */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? []).join(" ");
}

/**
 * Turning a second factor on, and off again.
 *
 * # Why enrolment is two calls
 *
 * `enroll` mints a secret and changes nothing; `confirm` proves the app and the
 * daemon agree and only then switches enforcement on. Every way this can go
 * wrong -- a secret copied with a character missing, an app on a phone whose
 * clock has drifted by two minutes, a QR nobody scanned because there is no QR
 * -- lands between the two calls, where the cost is pressing a button again. A
 * one-call version puts all of them *after* enforcement, where the cost is an
 * account nobody can sign in to and an operator with a shell.
 *
 * # What is deliberately not here
 *
 * A QR code. Rendering one means an encoder -- Reed-Solomon, mask selection,
 * the lot -- and this project does not add an npm dependency to a client that
 * renders hostile mail for a picture of a string it is already showing. The URI
 * is on screen and can be opened on the device it is being read on, and the
 * secret is grouped for typing. If a scan is wanted later it is a vendored pure
 * function, not a package.
 */
function TwoFactor() {
  const [status, setStatus] = useState<auth.TotpStatus | null>(null);
  // The daemon's own sentence about a deployment with no master key, kept
  // rather than turned into a boolean: it says which environment variable an
  // operator has not set, and this screen could only paraphrase that worse.
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<auth.TotpEnrolment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; bad: boolean } | null>(null);

  useEffect(() => {
    auth
      .totpStatus()
      .then(setStatus)
      .catch((e: unknown) => {
        if (auth.problemOf(e) === auth.TOTP_UNAVAILABLE) {
          setUnavailable(e instanceof Error ? e.message : String(e));
          return;
        }
        // Anything else is a daemon that is briefly unreachable or older than
        // these routes. The card stays in its loading state rather than
        // claiming "off", which is the answer somebody would act on by
        // enrolling a second time.
        setNote({ text: e instanceof Error ? e.message : String(e), bad: true });
      });
  }, []);

  /**
   * Run one of the four calls, and put whatever it says on the screen.
   *
   * The 503 is caught here as well as on load, because a deployment can lose
   * its master key between the two -- a restart with the variable dropped from
   * the unit file -- and the difference between "that did not work" and "this
   * server cannot do this at all" is the difference between pressing the button
   * again and going to find an operator.
   */
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setNote(null);
    try {
      await work();
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      if (auth.problemOf(e) === auth.TOTP_UNAVAILABLE) setUnavailable(text);
      else setNote({ text, bad: true });
    } finally {
      setBusy(false);
    }
  };

  const begin = () =>
    void run(async () => {
      // The current code, but only where there is a current authenticator to
      // produce one. Sent as a field only when present -- see `totpEnroll`, and
      // the reason the daemon insists on it: a borrowed session must not be
      // able to move somebody's second factor to another phone.
      const minted = await auth.totpEnroll(status?.enabled ? code.trim() : undefined);
      setEnrolment(minted);
      // The field is about to be asked a different question -- a code from the
      // *new* secret -- and leaving the old answer in it invites somebody to
      // press Confirm on a code that cannot possibly work.
      setCode("");
    });

  const confirm = () =>
    void run(async () => {
      await auth.totpConfirm(code.trim());
      setEnrolment(null);
      setCode("");
      // Set from what just happened rather than re-fetched. A confirm that
      // returned 204 *is* the account being enrolled, and a second round trip
      // to be told so would leave the card showing the old state for as long as
      // it took -- on the one screen where that reads as the button not having
      // worked.
      setStatus({ enabled: true, pending: false });
      setNote({
        text: "Two-factor sign-in is on. New sign-ins will ask for a code.",
        bad: false,
      });
    });

  const disable = () =>
    void run(async () => {
      await auth.totpDisable(code.trim());
      setCode("");
      setStatus({ enabled: false, pending: false });
      setNote({ text: "Two-factor sign-in is off.", bad: false });
    });

  const panel = totpPanel({ status, unavailable: unavailable !== null, enrolment });

  return (
    <div class="card">
      <h2>Two-factor sign-in</h2>

      {panel === "loading" && !note && <span class="spinner" />}

      {panel === "unavailable" && (
        <p class="hint" style={{ marginBottom: 0 }}>
          {/* The daemon's sentence, then ours. Theirs names the missing key;
              ours says whose problem it is, because a person reading a settings
              page has no way to know that this is a server that was never set
              up for it rather than a control that is broken. */}
          {unavailable} Not available on this server.
        </p>
      )}

      {panel === "off" && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            Off. Signing in will also ask for a six-digit code from an authenticator app.
          </span>
          <button class="btn primary" disabled={busy} onClick={begin}>
            {busy ? "Setting up…" : "Set up"}
          </button>
        </div>
      )}

      {panel === "restart" && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            A setup was started and never finished. Starting again replaces it.
          </span>
          <button class="btn primary" disabled={busy} onClick={begin}>
            {busy ? "Setting up…" : "Start again"}
          </button>
        </div>
      )}

      {panel === "enrolling" && enrolment && (
        <>
          <p class="hint" style={{ marginTop: 0 }}>
            Add this to an authenticator app, then enter a code from it to finish.
          </p>
          {/* Both spellings, because the two routes fail in different places: a
              link is the whole thing in one tap on the device holding the app,
              and the secret is what somebody types when the app is on a
              different phone from the browser. */}
          <div class="field">
            <label>Setup link</label>
            <div class="code" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {enrolment.uri}
            </div>
            {/* An ordinary link with a scheme the app registers, so it is the
                one-tap route on a phone and does nothing surprising anywhere
                else. Deliberately not a button: this is a navigation, and
                handing the URI to the platform is the whole of what it does. */}
            <a class="hint" href={enrolment.uri}>
              Open it in an authenticator app on this device
            </a>
          </div>
          <div class="field">
            <label>Or type this secret in by hand</label>
            <div class="code">{groupSecret(enrolment.secret)}</div>
            <span class="hint">
              Keep this somewhere safe: it is the only way back in without the phone.
            </span>
          </div>
          <div class="field">
            <label for="totp-confirm">Code from the app</label>
            <input
              id="totp-confirm"
              type="text"
              name="one-time-code"
              inputMode="numeric"
              autocomplete="one-time-code"
              maxLength={6}
              value={code}
              disabled={busy}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            />
          </div>
          {note && (
            <p class="hint" style={note.bad ? { color: "var(--danger)" } : undefined}>
              {note.text}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button class="btn primary" disabled={busy || !code.trim()} onClick={confirm}>
              {busy ? "Checking…" : "Confirm"}
            </button>
            {/* Abandoning leaves the pending secret on the daemon, which is why
                this says "cancel" and not "delete": nothing is enforced either
                way, and the next setup replaces it. Claiming to have removed
                something this screen cannot remove would be a worse lie than
                the one it saves. */}
            <button
              class="btn ghost"
              disabled={busy}
              onClick={() => {
                setEnrolment(null);
                setCode("");
                setNote(null);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {panel === "on" && (
        <>
          <p class="hint" style={{ marginTop: 0 }}>
            On. New sign-ins ask for a code as well as your password. The buttons below need a
            current code.
          </p>
          <div class="field">
            <label for="totp-current">Current code</label>
            <input
              id="totp-current"
              type="text"
              name="one-time-code"
              inputMode="numeric"
              autocomplete="one-time-code"
              maxLength={6}
              value={code}
              disabled={busy}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            />
          </div>
          {note && (
            <p class="hint" style={note.bad ? { color: "var(--danger)" } : undefined}>
              {note.text}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button class="btn" disabled={busy || !code.trim()} onClick={begin}>
              Move to a new app
            </button>
            <button class="btn" disabled={busy || !code.trim()} onClick={disable}>
              Turn off
            </button>
          </div>
        </>
      )}

      {/* The states that draw their own note draw it beside their fields, where
          the code that caused it still is. This is for the two that do not. */}
      {note && (panel === "off" || panel === "restart" || panel === "loading") && (
        <p class="hint" style={note.bad ? { color: "var(--danger)" } : undefined}>
          {note.text}
        </p>
      )}
    </div>
  );
}

/**
 * The reference page: what this app is, what the keys do, where the operator's
 * work happens, and the token that is not part of any of it.
 *
 * Not peers of Appearance, which is why they are not in the group list beside
 * it. Nobody comes to settings to read about the app; they come to change
 * something, find they cannot, and want to know where it is instead -- and all
 * four of these are answers to that question.
 */
export function About() {
  return (
    <>
      {/*
        It talks to one daemon, over the same origin that served it, and never
        to a mail provider directly -- that is the daemon's job, and it is why
        no provider password ever reaches this device. Only the last of those
        is a fact about the reader, so it is the only one on screen.
      */}
      <p class="lede">
        A thin JMAP client for one HydraMail daemon. No provider password ever reaches this device.
      </p>

      <Keyboard />
      <Software />
      <EmailLinks />
      <Operator />
      <Advanced />
    </>
  );
}

/**
 * The version, and how it changes.
 *
 * Two entirely different cards behind one name, because they are answers to the
 * same question asked from two places. The desktop app can replace itself, so it
 * says what it is and offers to look for something newer. A browser cannot be
 * out of date -- loading the page *is* the update -- so it has nothing to say
 * about itself and instead offers the desktop build to somebody who might want
 * it.
 *
 * `updates` decides which, and that is the whole gate: it is `null` in a browser
 * and an object on the desktop, so neither card can appear in the build the
 * other belongs to. Captured into a local first, because the null check has to
 * survive into the child that uses it.
 */
function Software() {
  const control = updates;
  return control ? <DesktopUpdate control={control} /> : <DesktopDownloadOffer />;
}

/**
 * Making this the app that opens email links -- the setting macOS otherwise
 * keeps inside Apple Mail.
 *
 * Drawn only where the OS can be asked: `defaultMailApp` is `null` in a
 * browser, and its answer is `null` on desktop platforms that cannot say (see
 * `src-tauri/src/mailapp.rs`). A button that could not work is worse than no
 * card. The status is read again after the button rather than assumed, because
 * macOS may ask the person first and they may say no.
 */
function EmailLinks() {
  const control = defaultMailApp;
  // undefined: not asked yet; null: this platform cannot say.
  const [isDefault, setIsDefault] = useState<boolean | null | undefined>(undefined);
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    void control?.isDefault().then(setIsDefault, () => setIsDefault(null));
  }, [control]);

  if (!control || isDefault == null) return null;

  async function makeDefault() {
    if (!control) return;
    setAsking(true);
    setFailed(null);
    try {
      await control.makeDefault();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    }
    setIsDefault(await control.isDefault().catch(() => null));
    setAsking(false);
  }

  return (
    <div class="card">
      <h2>Email links</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          {isDefault ? "HydraMail opens email links." : "Email links open in another app."}
        </span>
        {!isDefault && (
          <button class="btn" disabled={asking} onClick={() => void makeDefault()}>
            {asking ? "Waiting…" : "Make HydraMail the default"}
          </button>
        )}
      </div>
      {failed && (
        <p class="hint" style={{ marginBottom: 0, color: "var(--danger)" }}>
          {failed}
        </p>
      )}
    </div>
  );
}

/**
 * What the check found, which is four states and not a boolean.
 *
 * "Nothing has been asked yet" and "asked, and there is nothing" have to be
 * distinguishable or the card claims to be up to date before it has looked --
 * which is the more dangerous of the two lies, because it is the reassuring one.
 */
type CheckOutcome =
  | { at: "idle" }
  | { at: "checking" }
  | { at: "current" }
  | { at: "available"; version: string; notes: string | null }
  | { at: "failed"; why: string };

function DesktopUpdate({ control }: { control: NonNullable<typeof updates> }) {
  const [running, setRunning] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CheckOutcome>({ at: "idle" });
  // Shared with the banner above the message list rather than local, so an
  // install started from one is visible on the other. Two copies of this state
  // meant opening Settings mid-download found a screen offering to start it.
  const installing = updateWatch.installing.value !== null;

  useEffect(() => {
    void control.version().then(setRunning);
  }, [control]);

  async function check() {
    setOutcome({ at: "checking" });
    try {
      const found = await control.check();
      setOutcome(
        found ? { at: "available", version: found.version, notes: found.notes } : { at: "current" },
      );
    } catch (e) {
      // Reported rather than swallowed. This one *was* asked for -- somebody
      // pressed the button -- and "nothing happened" is the answer that sends
      // them to press it again.
      setOutcome({ at: "failed", why: e instanceof Error ? e.message : String(e) });
    }
  }

  async function install() {
    try {
      // `updateWatch.install` owns the in-flight state and clears it on
      // failure; on the happy path it deliberately does not, because the app is
      // restarting and a screen that flipped back to "install" in the last
      // moment would report the opposite of what just happened.
      await updateWatch.install(control);
    } catch (e) {
      setOutcome({ at: "failed", why: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <div class="card">
      <h2>Version</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          {/* The version is a round trip to the other process, so it is briefly
              absent. A dash rather than a spinner: this is one short string in a
              row that has other things in it, and a spinner in the middle of a
              sentence reads as something being wrong. */}
          HydraMail {running ?? "—"}
        </span>
        <button class="btn" disabled={outcome.at === "checking" || installing} onClick={() => void check()}>
          {outcome.at === "checking" ? "Checking…" : "Check for updates"}
        </button>
      </div>

      {outcome.at === "current" && (
        <p class="hint" style={{ marginBottom: 0 }}>
          Up to date.
        </p>
      )}

      {outcome.at === "failed" && (
        <p class="hint" style={{ marginBottom: 0, color: "var(--danger)" }}>
          {outcome.why}
        </p>
      )}

      {outcome.at === "available" && (
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: 12,
          }}
        >
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            Version {outcome.version} is available.
            {outcome.notes ? ` ${outcome.notes}` : ""}
          </span>
          {/* The button says it restarts, because it does -- and this is the only
              thing in the app that closes the window somebody is working in. An
              update that arrived without being asked for, or that restarted
              without saying so, would be the app taking a decision that is not
              its to take. See `UpdateControl` in `lib/net.types.ts`. */}
          <button class="btn primary" disabled={installing} onClick={() => void install()}>
            {updateWatch.installing.value !== null
              ? updateWatch.describe(updateWatch.installing.value)
              : "Install and restart"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The desktop build, for somebody reading this in a browser.
 *
 * Absent rather than disabled when the daemon serves no releases, which is the
 * rule every optional thing on these screens follows -- and here it is not even
 * a close call: a greyed-out "Download" is a question about the *server* asked
 * of the person least able to answer it.
 *
 * Fetched on render and never cached. The URL in the manifest is signed and
 * expires in about a quarter of an hour; a page left open for an afternoon would
 * otherwise hold a link that quietly stopped working. See `lib/release.ts`.
 */
function DesktopDownloadOffer() {
  const [offers, setOffers] = useState<DesktopDownload[]>([]);

  useEffect(() => {
    void desktopDownloads().then(setOffers);
  }, []);

  if (offers.length === 0) return null;

  return (
    <div class="card">
      <h2>Desktop app</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          Desktop app with an offline copy of your mail.
        </span>
        {/* Anchors and not buttons: a download is a navigation, and handing
            the URL to the browser is what gets the platform's own download
            machinery -- the progress, the resume, the file in the folder they
            expect. `rel` because the link leaves this origin for a signed URL.
            Every released platform is offered -- people fetch installers for
            the machine next to them -- with the visitor's own first and solid,
            and the rest ghosted behind it. */}
        {offers.map((offer, i) => (
          <a
            key={offer.os}
            class={i === 0 ? "btn" : "btn ghost"}
            href={offer.url}
            rel="noreferrer"
            download
          >
            HydraMail for {offer.os === "macos" ? "macOS" : "Linux"} {offer.version}
          </a>
        ))}
        {/* The build is ad-hoc signed, not notarized, so the browser's download
            gets the quarantine attribute and Gatekeeper refuses to open it.
            Said here, once, at the moment of downloading -- the alternative is
            a dialog on the Mac that names no fix at all. Updates the app
            fetches for itself are never quarantined, so this is first-run only. */}
        {offers.some((o) => o.os === "macos") && (
          <span class="hint" style={{ width: "100%" }}>
            First install on a Mac: unzip, drag HydraMail.app to Applications, then run{" "}
            <code>xattr -dr com.apple.quarantine /Applications/HydraMail.app</code>.
          </span>
        )}
      </div>
    </div>
  );
}

function Account() {
  const who = auth.identity.value;
  return (
    <div class="card">
      <h2>Account</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          {who ? (
            <>
              Signed in as <strong>{who.username ?? who.tenant}</strong>
              {who.displayName && who.displayName !== who.tenant ? ` (${who.displayName})` : ""} —{" "}
              {plural(store.accounts.value.length, "mail account")}.
            </>
          ) : (
            "Not signed in."
          )}
        </span>
        <button class="btn" onClick={() => void auth.logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * Notifications for this device, and the devices that already have them.
 *
 * This used to render nothing at all -- not a disabled toggle, not an
 * explanation -- on a daemon whose `/push` routes 404, because it was one card
 * in a stack and a control that cannot work is a support question. As a page
 * with its own name in the group list it cannot do that: someone who chose
 * Notifications asked the question outright, and an empty page is the one
 * answer that leaves them looking for the setting they think they have missed.
 * So the unsupported case says so, and only the moment before the daemon has
 * answered draws nothing.
 *
 * Permission is asked from the button below and from nowhere else. `denied` is
 * final -- no page can undo it and no browser offers a second prompt -- so the
 * only honest thing to say in that state is where the setting is, which is a
 * different place in every browser and is not somewhere this page can send
 * anyone.
 */
export function Notifications() {
  return (
    <>
      <PushDevices />
      <WhichMailNotifies />
    </>
  );
}

/**
 * Which mail is allowed to interrupt anybody, for the whole tenant.
 *
 * Its own component, and rendered outside [`PushDevices`]'s early returns on
 * purpose. That card answers "is this browser being notified", which is a
 * property of this device; this one is a property of the person, and it still
 * governs the phone when the browser is not enrolled and even when the daemon
 * has no transport at all -- which is exactly why `/push/prefs` is the one
 * route in that module not gated on `dispatcher()`.
 */
function WhichMailNotifies() {
  const [prefs, setPrefs] = useState<push.NotifyPrefs | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    push
      .getPrefs()
      .then(setPrefs)
      // A daemon older than the route, or one briefly unreachable. Rendering a
      // checkbox whose position is a guess is worse than rendering nothing:
      // both guesses are wrong in a way somebody would act on.
      .catch(() => setPrefs(null));
  }, []);

  if (!prefs) return null;

  async function save(next: Partial<push.NotifyPrefs>) {
    if (!prefs) return;
    setBusy(true);
    setProblem(null);
    try {
      // The daemon's answer, not the argument: it replaces rather than merges
      // and may narrow what it was sent.
      setPrefs(await push.setPrefs({ ...prefs, ...next }));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const choose = (notifyBulk: boolean) => save({ notifyBulk });

  return (
    <div class="card">
      <label style={{ display: "block", marginBottom: 14 }}>
        <strong>How much a notification says</strong>
        <div class="hint" style={{ marginBottom: 6 }}>
          Shown before the device is unlocked.
        </div>
        <select
          disabled={busy}
          value={prefs.detail}
          onChange={(e) => void save({ detail: (e.target as HTMLSelectElement).value })}
        >
          <option value="none">Just that mail arrived</option>
          <option value="sender">Who it is from</option>
          <option value="sender_and_subject">Sender and subject</option>
          <option value="sender_subject_and_preview">Sender, subject and the first line</option>
        </select>
        {prefs.detail === "sender_subject_and_preview" && (
          <div class="hint" style={{ marginTop: 6 }}>
            Phones stop at the subject.
          </div>
        )}
      </label>

      <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <input
          type="checkbox"
          style={{ width: "auto", marginTop: 3 }}
          disabled={busy}
          checked={prefs.notifyBulk}
          onChange={(e) => void choose((e.target as HTMLInputElement).checked)}
        />
        <span>
          <strong>Notify me about newsletters and mailing lists</strong>
          <div class="hint">
            Mailing lists, newsletters, automatic replies and delivery failures. Spam stays
            silent either way.
          </div>
          <div class="hint">Applies to every device signed in to this account.</div>
        </span>
      </label>
      {problem && (
        <p class="hint" style={{ color: "var(--danger)" }}>
          {problem}
        </p>
      )}
    </div>
  );
}

function PushDevices() {
  const [rows, setRows] = useState<push.RegistrationRow[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const load = () => {
    push
      .listRegistrations()
      .then(setRows)
      .catch(() => setRows(null));
  };

  useEffect(() => {
    void push.probe().then(() => {
      if (push.supported.value === "yes") load();
    });
  }, []);

  /*
   * The desktop shell is not a browser and must not be spoken to as one.
   *
   * In the packaged app the notifying is done by this build's own Rust side,
   * which watches the local replica and posts to the system directly -- Web
   * Push, the service worker and `Notification.requestPermission` are all
   * machinery that either does not exist in the webview or would enrol a
   * second, redundant channel. This page used to fall through to the browser
   * flow anyway and told desktop users their "browser" could not receive
   * notifications, which was a sentence about the wrong program entirely.
   */
  const desktop = windowChrome !== null;

  // `unknown` is the startup value and lasts one round trip. A spinner rather
  // than a placeholder card, for the same reason as before: a control that
  // appears and then vanishes is worse than one that arrives a moment late.
  // Not on the desktop, whose own card depends on nothing the daemon knows.
  if (!desktop && push.supported.value === "unknown") return <span class="spinner" />;

  if (!desktop && push.supported.value === "no") {
    return (
      <div class="card">
        <p class="hint" style={{ margin: 0 }}>
          Notifications are not configured on this server.
        </p>
      </div>
    );
  }

  const enrolment = push.enabled.value;
  const permission = push.permission.value;

  async function turnOn() {
    setNote(null);
    await push.enable();
    if (push.enabled.value) load();
  }

  async function turnOff() {
    setNote(null);
    await push.disable();
    load();
  }

  async function test() {
    setTesting(true);
    setNote(null);
    try {
      await push.sendTest();
      setNote("Sent. If nothing arrives, the daemon's log will say why.");
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  const deviceList = rows && rows.length > 0 && (
    <>
      <p class="hint" style={{ marginBottom: 0, marginTop: 14 }}>
        Devices being notified
      </p>
      {rows.map((row) => (
        <div
          key={row.id}
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "8px 0",
            borderTop: "1px solid var(--border)",
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong>{row.label}</strong>
            {row.id === enrolment?.id && (
              <span class="tag account" style={{ marginLeft: 8 }}>
                this device
              </span>
            )}
            <div class="hint">
              {row.transport} ·{" "}
              {/* Never delivered to is not the same as broken: a device
                  enrolled this morning on a quiet mailbox has never been
                  pushed, and saying "failing" about it would be a lie. */}
              {row.lastOkAt
                ? `last reached ${shortDate(new Date(row.lastOkAt * 1000).toISOString())}`
                : "not notified yet"}
            </div>
          </span>
          {/* No button on this device's own row: the control above already
              does it, and it does the other half too -- unsubscribing in
              this browser as well as deleting the row. Two buttons that
              are not the same thing but look like it is worse than one. */}
          {row.id !== enrolment?.id && (
            <button
              class="btn"
              disabled={push.busy.value}
              onClick={() =>
                void push.removeRegistration(row.id).then(load, (e: unknown) =>
                  setNote(e instanceof Error ? e.message : String(e)),
                )
              }
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </>
  );

  if (desktop) {
    return (
      <div class="card">
        <p class="hint" style={{ marginTop: 0 }}>
          This app notifies directly; nothing to enrol.
        </p>
        {/Mac/.test(navigator.platform) && (
          <p class="hint">
            If nothing appears, check System Settings → Notifications → HydraMail.
          </p>
        )}
        {push.supported.value === "yes" ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span class="hint" style={{ flex: 1, minWidth: 200 }}>
              The devices below are notified by the server when no app is open.
            </span>
            <button class="btn" disabled={testing} onClick={() => void test()}>
              {testing ? "Sending…" : "Send a test"}
            </button>
          </div>
        ) : push.supported.value === "no" ? (
          <p class="hint">
            No push transport is configured on this server, so phones are not notified.
          </p>
        ) : null}
        {note && <p class="hint">{note}</p>}
        {deviceList}
      </div>
    );
  }

  return (
    <div class="card">
      <p class="hint" style={{ marginTop: 0 }}>
        Notifies when mail arrives and the app is not open.
      </p>

      {/* The way back in for anyone who dismissed the strip on the mailbox. A
          dismissal there is permanent by design, and a permanent dismissal with
          nowhere else to go is a feature someone can lose by accident. */}
      {install.installable.value && !install.installed.value && (
        <div
          style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}
        >
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            Opens in a window of its own.
          </span>
          <button class="btn" onClick={() => void install.promptInstall()}>
            Install
          </button>
        </div>
      )}

      {!push.browserCapable ? (
        <p class="hint">
          {install.isIOS ? (
            <>
              On iOS, tap Share, then <em>Add to Home Screen</em>, and open HydraMail from the
              icon to receive notifications.
            </>
          ) : (
            "This browser cannot receive notifications."
          )}
        </p>
      ) : permission === "denied" ? (
        <p class="hint">
          Notifications are blocked for this site. Allow them in the browser's site settings.
        </p>
      ) : enrolment ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            On for this device.
          </span>
          <button class="btn" disabled={testing} onClick={() => void test()}>
            {testing ? "Sending…" : "Send a test"}
          </button>
          <button class="btn" disabled={push.busy.value} onClick={() => void turnOff()}>
            Turn off
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span class="hint" style={{ flex: 1, minWidth: 200 }}>
            Off for this device.
            {permission === "granted" && " This browser has already given permission."}
          </span>
          <button class="btn primary" disabled={push.busy.value} onClick={() => void turnOn()}>
            {push.busy.value ? "Turning on…" : "Turn on"}
          </button>
        </div>
      )}

      {(push.problem.value ?? note) && (
        <p class="hint" style={push.problem.value ? { color: "var(--danger)" } : undefined}>
          {push.problem.value ?? note}
        </p>
      )}

      {deviceList}
    </div>
  );
}

/**
 * Every credential that can currently read this mailbox, and a button to end
 * each one.
 *
 * This is the screen that makes a stolen laptop recoverable without an
 * operator, and it is why sessions are worth listing at all rather than just
 * expiring quietly.
 */
function Sessions() {
  const [rows, setRows] = useState<auth.SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    auth
      .listSessions()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(load, []);

  async function end(row: auth.SessionRow) {
    try {
      await auth.revokeSession(row.id);
      // Ending your own session is signing out, and the cookie the browser
      // still holds is now dead -- so say so rather than leaving the app to
      // discover it on the next poll.
      if (row.current) {
        await auth.logout();
        return;
      }
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div class="card">
      <h2>Signed-in devices</h2>
      {error && (
        <p class="hint" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}
      {rows === null && !error && <span class="spinner" />}
      {rows?.length === 0 && <p class="hint">Not signed in.</p>}
      {rows?.map((row) => (
        <div
          key={row.id}
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "8px 0",
            borderTop: "1px solid var(--border)",
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong>{row.label}</strong>
            {row.current && <span class="tag account" style={{ marginLeft: 8 }}>this device</span>}
            <div class="hint">
              {/* An enrolled device is a token someone pasted into a script or a
                  config, so saying "signed in 3 days ago" about it would be
                  misleading -- it never signed in at all. */}
              {row.origin === "cli" ? "Enrolled token" : "Password sign-in"} ·{" "}
              {row.lastSeenAt
                ? `last used ${shortDate(new Date(row.lastSeenAt * 1000).toISOString())}`
                : "never used"}
              {row.expiresAt
                ? ` · expires ${shortDate(new Date(row.expiresAt * 1000).toISOString())}`
                : " · does not expire"}
            </div>
          </span>
          <button class="btn" onClick={() => void end(row)}>
            {row.current ? "Sign out" : "End"}
          </button>
        </div>
      ))}
    </div>
  );
}

function Password() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState<{ text: string; bad: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    if (next !== confirm) {
      // Checked here rather than at the server: the server has no way to know
      // the two boxes disagreed, and would just accept whichever one it got.
      setNote({ text: "The two new passwords do not match.", bad: true });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      await auth.changePassword(current, next);
      setNote({ text: "Password changed. Other devices have been signed out.", bad: false });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setNote({ text: err instanceof Error ? err.message : String(err), bad: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="card">
      <h2>Password</h2>
      <form onSubmit={submit}>
        <div class="field">
          <label for="cur">Current password</label>
          <input
            id="cur"
            type="password"
            autocomplete="current-password"
            value={current}
            disabled={busy}
            onInput={(e) => setCurrent((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="new">New password</label>
          <input
            id="new"
            type="password"
            autocomplete="new-password"
            value={next}
            disabled={busy}
            onInput={(e) => setNext((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="new2">New password again</label>
          <input
            id="new2"
            type="password"
            autocomplete="new-password"
            value={confirm}
            disabled={busy}
            onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
          />
        </div>
        {note && (
          <p class="hint" style={note.bad ? { color: "var(--danger)" } : undefined}>
            {note.text}
          </p>
        )}
        <button class="btn primary" type="submit" disabled={busy || !current || !next}>
          {busy ? "Changing…" : "Change password"}
        </button>
        {/*
          Every other device, and only the others: a password changed because a
          laptop went missing has to reach that laptop to have achieved
          anything, and signing this one out as well would only be theatre.
        */}
        <p class="hint" style={{ marginTop: 10 }}>
          Signs out every other device.
        </p>
      </form>
    </div>
  );
}

/**
 * The way in to the shortcut sheet.
 *
 * `?` opens it too and is the faster route, but it only ever helps someone who
 * already suspects there is something to find -- which is the whole problem
 * with keyboard shortcuts and the reason the sheet was worth writing down. This
 * card answers "does this thing have shortcuts" where people go to ask it, and
 * names the key so it is asked only once.
 */
function Keyboard() {
  return (
    <div class="card">
      <h2>Keyboard</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          Press <kbd>?</kbd> anywhere in the app for this list.
        </span>
        <button class="btn" onClick={() => (nav.helpOpen.value = true)}>
          Show shortcuts
        </button>
      </div>
    </div>
  );
}

/**
 * The address book's front door.
 *
 * Pruning the *book* is a settings job: it is about what this app will suggest
 * for the next year, not about the message in front of you. Pruning *one
 * suggestion* is not, and stays where it was noticed -- the × on a row as it is
 * offered, and the Delete key that does the same thing, both in
 * `RecipientField.tsx`.
 *
 * Which is why the copy below names that route rather than leaving this screen
 * to read as the only way to remove an address. It is not, and a settings page
 * that quietly claims to be the only door sends people here for a job they
 * could have done without leaving the message they were writing.
 */
function SuggestionBook() {
  return (
    <div class="card">
      <h2>Recipient suggestions</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span class="hint" style={{ flex: 1, minWidth: 200 }}>
          Addresses collected from your mail, suggested as you type. Removing one only stops the
          suggestion.
        </span>
        <button class="btn" onClick={() => (nav.suggestionsOpen.value = true)}>
          Manage suggestions
        </button>
      </div>
    </div>
  );
}

/**
 * Where operator work went, and why it is not here.
 *
 * This screen used to have an admin-token field. It was the wrong thing to ask
 * of anyone: that token administers every tenant on the server — creating and
 * deleting them, reading their metrics — and it was being typed into the origin
 * that renders untrusted mail HTML. Managing your own mail accounts needs none
 * of it any more, so there is nothing left the field was for, and a credential
 * with no purpose in an origin is a credential that can only be stolen from it.
 *
 * A link rather than an embedded panel, and a different port rather than a
 * different path, because an origin is the unit a browser actually isolates.
 */
function Operator() {
  return (
    <div class="card">
      <h2>Running this server</h2>
      <p class="lede" style={{ margin: 0 }}>
        Tenants, operator tokens and server-wide settings live in the admin panel, on a port of its
        own — <code>http://localhost:8081</code> in the default deployment. It takes an operator
        token: issue one with <code>hydramaild admin token issue --label panel</code>. Your own mail
        accounts are on the Accounts screen.
      </p>
    </div>
  );
}

/**
 * The device-token field, demoted.
 *
 * Still the documented way a script or another JMAP client reaches this daemon,
 * which is why it stays; behind a disclosure because it is no longer how a
 * person signs in, and leading with it taught everyone that pasting hex was the
 * normal thing to do.
 */
function Advanced() {
  return (
    <details class="card">
      <summary>
        <h2 style={{ display: "inline", fontSize: "inherit" }}>Advanced: device token</h2>
      </summary>
      <p class="lede" style={{ marginTop: 10 }}>
        Not needed to read mail. This is for scripts and for JMAP clients that are not this page.
      </p>
      <div class="field">
        <label for="tok">Device token</label>
        {/*
          Deliberately not `type="password"`, and named nothing like a login.

          It was a password box, and a password manager duly offered to fill it
          -- which wrote a password into the value that overrides the session
          cookie on every request. The result was an account that signed in
          successfully and was refused by everything afterwards, on a page the
          URL restores after each reload, so the loop closed on itself.
          `autocomplete="off"` does not stop this; browsers ignore it on
          credential-shaped fields precisely because sites used to abuse it.

          A token is not a password: it is issued rather than remembered, and
          there is nothing here worth hiding from someone already reading the
          logged-in settings page on this device.
        */}
        <input
          id="tok"
          type="text"
          name="hm-device-token"
          value={deviceToken.value}
          placeholder="hm_…"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck={false}
          onInput={(e) => (deviceToken.value = (e.target as HTMLInputElement).value.trim())}
        />
        {/* Sent as a bearer header, which is what beats the session cookie. */}
        <span class="hint">
          Overrides your session while set. Issue one per device:{" "}
          <code>hydramaild device enroll &lt;tenant&gt; --label "laptop"</code>
        </span>
        {deviceToken.value && !looksLikeToken(deviceToken.value) && (
          <span class="hint" style={{ color: "var(--danger)" }}>
            Not a device token, so it is ignored. A token reads <code>hm_</code> followed by 16
            then 64 hex characters.
          </span>
        )}
      </div>
      {store.startupError.value && (
        <span class="hint" style={{ color: "var(--danger)" }}>
          {store.startupError.value}
        </span>
      )}
    </details>
  );
}

/**
 * The undo window.
 *
 * The card explains one thing and it is the thing that makes the setting
 * trustworthy: the delay is applied by the server, not by this page. A client
 * that held a message back itself would have an Undo button over a message the
 * daemon had already sent, and would lose the message entirely if the tab were
 * closed during the countdown. What this preference actually does is put a
 * release time on each submission, which is a field RFC 8621 defines and this
 * daemon honours ahead of its own default.
 *
 * "This server decides" is the default and a real option rather than a polite
 * way of saying nothing. The hold is an operator setting and is not published
 * to clients, so it is the only choice here that can honestly claim to match
 * whatever the deployment does -- it sends no release time at all. Naming a
 * number overrides it per message; naming zero says there is to be no window,
 * in the words the protocol has for that.
 */
function UndoSend() {
  const chosen = undoSendSeconds.value;
  // Remembered across a switch to the server default, so going and coming back
  // does not silently reset a number somebody picked.
  const [last, setLast] = useState(chosen === null || chosen <= 0 ? 10 : chosen);
  const mode = chosen === null ? "server" : chosen <= 0 ? "off" : "delay";

  return (
    <div class="card">
      <h2>Undo send</h2>
      <p class="hint" style={{ marginTop: 0 }}>
        Sent messages wait in the outbox and can be taken back until then.
      </p>

      <Row label="Undo send">
        <Segmented
          value={mode}
          options={[
            ["server", "This server decides"],
            ["delay", "Wait"],
            ["off", "Off"],
          ]}
          onPick={(v) => {
            undoSendSeconds.value = v === "server" ? null : v === "off" ? 0 : last;
          }}
        />
      </Row>

      {mode === "delay" && (
        <Row label="">
          <input
            class="autoread-secs"
            type="number"
            min="1"
            max={UNDO_SEND_MAX}
            step="1"
            inputMode="numeric"
            aria-label="Seconds a message waits before it is sent"
            value={chosen ?? last}
            onInput={(e) => {
              const raw = Number((e.target as HTMLInputElement).value);
              if (Number.isNaN(raw)) return;
              const clamped = Math.min(UNDO_SEND_MAX, Math.max(1, Math.round(raw)));
              setLast(clamped);
              undoSendSeconds.value = clamped;
            }}
          />
          <span class="autoread-unit">seconds</span>
        </Row>
      )}

      <p class="hint">
        {mode === "server"
          ? "The server's default: ten seconds unless changed."
          : mode === "off"
            ? "Sent immediately, with nothing to undo."
            : "Every message waits this long, with Undo in the outbox."}
      </p>
    </div>
  );
}

/**
 * One labelled setting.
 *
 * Classes rather than the inline styles this used to carry, and the difference
 * is not tidiness: an inline style cannot be answered by a media query, so the
 * fixed 130px label and the side-by-side row applied at every width. On a
 * 344px cover screen that left a three-option control 158px to draw in and
 * "Dark" rendered as "Da". The stacked phone layout lives in `styles.css`
 * because that is the only place it can live.
 */
function Row({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="set-row">
      <span class="set-label">{label}</span>
      {children}
    </div>
  );
}

function Segmented({
  value,
  options,
  onPick,
}: {
  value: string;
  options: [string, string][];
  onPick: (v: string) => void;
}) {
  return (
    <div class="seg">
      {options.map(([key, label]) => (
        <button key={key} aria-pressed={value === key} onClick={() => onPick(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * What one direction of swipe is bound to.
 *
 * A `<select>` rather than the `Segmented` control every other row on this page
 * uses, and the count is the whole reason: five options against the four that
 * already cut "Dark" down to "Da" on a 344px cover screen -- see the stacked
 * layout in `styles.css`. A segmented control is a row of words that has to be
 * readable all at once, and past four short ones it stops being one. The
 * `<select>` also gets the platform's own picker on the device this setting is
 * for, which is the control a thumb is best at.
 *
 * `aria-label` because `Row` draws its label as a `<span>` and not a
 * `<label for>`: without it a screen reader announces a combo box with no name
 * at all. The spoken label says "a message" where the visible one is beside the
 * word "Swipe left" and does not need to.
 */
function SwipeBinding({ label, bound }: { label: string; bound: Signal<SwipeAction> }) {
  return (
    <select
      aria-label={label}
      value={bound.value}
      onChange={(e) => (bound.value = (e.target as HTMLSelectElement).value as SwipeAction)}
    >
      {SWIPE_ACTIONS.map(([action, text]) => (
        <option key={action} value={action}>
          {text}
        </option>
      ))}
    </select>
  );
}

/**
 * How long a message stays open before it is marked read.
 *
 * A number of seconds and not a set of presets, because the useful value is
 * personal and narrow: someone who reads a subject and moves on wants half a
 * second, someone who triages with `j` and `k` wants four, and a preset list
 * would either miss both or be long enough to need reading.
 *
 * Never is a checkbox rather than a zero-ish sentinel in the same field. Zero
 * already means something here -- mark it read the instant it opens -- so a
 * field where 0 meant "immediately" and blank meant "never" would put two
 * opposite answers a keystroke apart.
 */
function AutoRead() {
  const off = autoReadSeconds.value === null;
  // Remembered across a toggle so unticking and reticking does not silently
  // reset a chosen delay to the default.
  const [last, setLast] = useState(autoReadSeconds.value ?? 1.5);

  return (
    <div class="autoread">
      <label class="autoread-on">
        <input
          type="checkbox"
          checked={!off}
          onChange={(e) => {
            const on = (e.target as HTMLInputElement).checked;
            autoReadSeconds.value = on ? last : null;
          }}
        />
        <span>Automatically, after</span>
      </label>
      <input
        class="autoread-secs"
        type="number"
        min="0"
        max={AUTO_READ_MAX}
        step="0.5"
        inputMode="decimal"
        disabled={off}
        aria-label="Seconds a message stays open before it is marked read"
        value={off ? last : autoReadSeconds.value!}
        onInput={(e) => {
          const raw = Number((e.target as HTMLInputElement).value);
          if (Number.isNaN(raw)) return;
          const clamped = Math.min(AUTO_READ_MAX, Math.max(0, raw));
          setLast(clamped);
          if (!off) autoReadSeconds.value = clamped;
        }}
      />
      <span class="autoread-unit">seconds</span>
      {/*
        The one thing someone setting this cannot be expected to know, and the
        reason zero is not the default: walking the list opens each row.
      */}
      <p class="hint">
        {off
          ? "Stays unread until you mark it read."
          : autoReadSeconds.value === 0
            ? "Marked read the moment it opens."
            : "Marked read after this long on screen."}
      </p>
    </div>
  );
}

/**
 * The senders in the low-priority inbox, and the way to add or remove one.
 *
 * # What this page is careful to say
 *
 * That nothing moved. Every rule here is a judgement this daemon keeps locally:
 * the mail is in exactly the folders the provider says it is in, with exactly
 * the flags it always had, on every other device the reader owns. Somebody
 * reading a list titled "low priority" will reasonably assume it is a filter
 * that files mail somewhere, and the one sentence at the top exists to correct
 * that before they act on it.
 *
 * And that it is retroactive in both directions. Adding a rule reaches
 * backwards through the whole archive, removing one puts every message back,
 * and both report how many moved -- which is the fastest way to learn what the
 * feature does.
 *
 * # Why the scope is a control on the row rather than a second kind of rule
 *
 * Widening one sender to their whole domain and narrowing it again is the edit
 * people actually make -- one newsletter turns out to be six addresses at the
 * same company -- and it is the same rule about the same correspondent
 * throughout. Making it a toggle on the row keeps that continuity, and keeps
 * the list a list of *people and organisations* rather than of match patterns.
 */
export function LowPriority() {
  const [rules, setRules] = useState<priority.Rule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    priority
      .list()
      .then((items) => {
        setRules(items);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(load, []);

  /*
   * Every write reloads the mail views as well as this list.
   *
   * A rule change moves messages between the two tabs and changes the counts
   * beside them, and this page is one column away from both. Leaving the mail
   * behind to catch up on its own would mean going back to a list that
   * disagrees with the page you just used -- so `store.reclassified` re-reads
   * the mailboxes and the list, and the sentence here reports what moved.
   */
  const after = async (result: priority.WriteResult, said: string) => {
    load();
    await store.reclassified();
    setNote(
      result.reclassified > 0
        ? `${said} — ${plural(result.reclassified, "message")} moved.`
        : `${said} — no stored message matched, so nothing moved.`,
    );
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const add = (e: Event) => {
    e.preventDefault();
    const pattern = draft.trim();
    if (!pattern) return;
    // An entry with no `@` can only be a domain, and asking somebody to pick
    // the scope from a menu before they have typed anything is a form that
    // asks a question ahead of the answer that decides it. The scope is
    // changeable on the row afterwards, which is where it belongs.
    const scope: priority.RuleScope = pattern.includes("@") ? "address" : "domain";
    void run(async () => {
      const result = await priority.create(pattern, scope);
      setDraft("");
      await after(result, `${pattern} is low priority`);
    });
  };

  return (
    <>
      <p class="lede">
        Mail from these senders goes to the Low priority tab of your inbox and never raises a
        notification. Nothing is moved or labelled on the mail server: this is a judgement kept
        here, so the messages are still in your inbox everywhere else you read mail.
      </p>

      <div class="card">
        <form onSubmit={add} class="lowprio-add">
          <label class="field">
            <span>Address or domain</span>
            <input
              type="text"
              value={draft}
              placeholder="deals@example.com or example.com"
              autocomplete="off"
              spellcheck={false}
              onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
            />
          </label>
          <button class="btn primary" type="submit" disabled={busy || !draft.trim()}>
            Add
          </button>
        </form>
        <p class="hint">
          A domain covers every address at it.
        </p>
      </div>

      {note && (
        <div class="banner" role="status">
          {note}
        </div>
      )}
      {error && (
        <div class="banner error" role="alert">
          {error}
        </div>
      )}

      {rules === null && !error ? (
        <span class="spinner" />
      ) : rules && rules.length === 0 ? (
        <div class="card">
          <p class="hint" style={{ margin: 0 }}>
            No senders yet. Add one from a message's “…” menu: Low priority.
          </p>
        </div>
      ) : (
        <div class="card">
          {(rules ?? []).map((rule) => (
            <div class="lowprio-row" key={rule.id}>
              <div class="lowprio-who">
                <strong>{rule.display}</strong>
                {/*
                  Shown only when normalisation changed something -- a Gmail
                  address written with dots, a domain in capitals. Somebody
                  whose rule reads exactly as they typed it does not need to be
                  told twice; somebody whose does not deserves to see what is
                  really being compared rather than deciding the rule is broken.
                */}
                {rule.pattern !== rule.display.toLowerCase() && (
                  <span class="hint">matches {rule.pattern}</span>
                )}
              </div>
              <label class="lowprio-scope">
                <input
                  type="checkbox"
                  checked={rule.scope === "domain"}
                  disabled={busy}
                  onChange={(e) => {
                    const wide = (e.target as HTMLInputElement).checked;
                    void run(async () => {
                      const result = await priority.setScope(rule.id, wide ? "domain" : "address");
                      await after(
                        result,
                        wide
                          ? `Everyone at ${result.rule?.pattern ?? rule.display} is low priority`
                          : `Only ${result.rule?.display ?? rule.display} is low priority`,
                      );
                    });
                  }}
                />
                <span>Whole domain</span>
              </label>
              <button
                class="btn ghost"
                disabled={busy}
                onClick={() => {
                  void run(async () => {
                    const result = await priority.remove(rule.id);
                    await after(result, `${rule.display} is back in Focused`);
                  });
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * How much of each account is kept on this computer.
 *
 * Only ever drawn on a build that has somewhere to keep it. `SettingsShell`
 * leaves the whole page out otherwise -- see the note there -- rather than
 * showing an empty one, because "no offline storage on this build" and
 * "offline storage, holding nothing" are different sentences and only the
 * second is worth a screen.
 */
export function Offline() {
  const accounts = store.accounts.value;
  const figures = retention.status.value;

  useEffect(() => {
    void retention.refreshAll();
  }, [accounts.length]);

  return (
    <>
      <p class="lede">
        Mail saved here can be read with no network. Every message's headers are held; the bodies
        are what a limit rations. When there is no room, the ones you read or received longest ago
        go first. Messages you have saved yourself stay whatever the limit says.
      </p>

      {accounts.map((account) => (
        <AccountOffline key={account.id} id={account.id} name={account.name} at={figures.get(account.id)} />
      ))}

      {accounts.length === 0 && <p class="lede">No accounts yet.</p>}
    </>
  );
}

/** The choices, in bytes. `null` is "whatever this build's default is". */
/**
 * How far back, per account, with "Default" last for the reason `LIMITS` gives.
 *
 * Two limits now, and they are an `and` rather than an `or`: an account fetches
 * until it meets whichever it reaches first. That is what makes 30 days the
 * useful default -- the size cap alone let a phone walk a seven-account archive
 * at 130MB an hour, because 512MB *per account* is 3.5GB and nothing was going
 * to reach it before the battery went.
 */
const AGES: [string, number | null][] = [
  ["1 week", 7],
  ["30 days", 30],
  ["3 months", 90],
  ["1 year", 365],
  ["No limit", retention.AGE_UNLIMITED],
  ["Default", null],
];

const LIMITS: [string, number | null][] = [
  ["Nothing", 0],
  ["100 MB", 100 * 1024 * 1024],
  ["500 MB", 500 * 1024 * 1024],
  ["2 GB", 2 * 1024 * 1024 * 1024],
  ["Default", null],
];

function AccountOffline({
  id,
  name,
  at,
}: {
  id: string;
  name: string;
  at: RetentionStatus | undefined;
}) {
  const [busy, setBusy] = useState(false);

  const choose = (bytes: number | null) => {
    setBusy(true);
    void retention.setCap(id, bytes).finally(() => setBusy(false));
  };

  const chooseAge = (days: number | null) => {
    setBusy(true);
    void retention.setAge(id, days).finally(() => setBusy(false));
  };

  // `null` is not a value the segmented control can hold -- it is "no override"
  // -- so the current cap is matched against the numbers and falls through to
  // Default when it is the one this build chose rather than one somebody did.
  const chosen = at ? (LIMITS.find(([, v]) => v === at.capBytes)?.[0] ?? "Default") : "Default";
  // Same fall-through as the cap: `null` means "no override", so a window that
  // matches no offered number is the build's default rather than a choice.
  const chosenAge = at ? (AGES.find(([, v]) => v === at.ageDays)?.[0] ?? "Default") : "Default";

  return (
    <div class="card">
      <h3>{name}</h3>
      {/* "Limit", not "Save": this row is how much may be saved, and reusing the
          verb from the reading pane's control would read as a second place to
          save something. */}
      <Row label="Limit">
        <Segmented
          value={chosen}
          options={LIMITS.map(([label]) => [label, label] as [string, string])}
          onPick={(picked) => {
            if (busy) return;
            const found = LIMITS.find(([label]) => label === picked);
            if (found) choose(found[1]);
          }}
        />
      </Row>
      {/* Under the size limit, because the two are read together: this row
          says how far back, that one says how much, and mail stops at
          whichever comes first. */}
      <Row label="How far back">
        <Segmented
          value={chosenAge}
          options={AGES.map(([label]) => [label, label] as [string, string])}
          onPick={(picked) => {
            if (busy) return;
            const found = AGES.find(([label]) => label === picked);
            if (found) chooseAge(found[1]);
          }}
        />
      </Row>
      <Row label="Using">
        <span>
          {at ? fileSize(at.usedBytes) : "—"}
          {at && at.capBytes > 0 && <span class="muted"> of {fileSize(at.capBytes)}</span>}
          {at && at.capBytes === 0 && <span class="muted"> — saving nothing</span>}
        </span>
      </Row>
      {at && at.pinned > 0 && (
        <Row label="Pinned">
          <span>
            {plural(at.pinned, "message")} kept whatever the limit says
          </span>
        </Row>
      )}
      {at && at.overBytes > 0 && (
        <p class="lede" style={{ marginTop: 4 }}>
          {fileSize(at.overBytes)} over the limit and not able to come down: what is left is either
          pinned, or the same message in another account, which shares one copy on disk. Unpin
          something, or raise the limit.
        </p>
      )}
    </div>
  );
}
